import React, { useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { DEFAULT_REVIEW_URL } from "@/lib/useGoogleReviews";

export function Stars({ value = 0, size = 16, testid }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`} data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} strokeWidth={1.2} className={i <= Math.round(value) ? "fill-brass text-brass" : "text-espresso/25"} />
      ))}
    </span>
  );
}

export function LeaveReviewButton({ testid = "leave-review-button", variant = "dark", className = "" }) {
  const { settings } = useSite();
  const styles = variant === "light" ? "bg-ivory text-espresso hover:bg-linen" : "bg-espresso text-ivory hover:bg-espressosoft";
  return (
    <a href={settings?.google_review_url || DEFAULT_REVIEW_URL} target="_blank" rel="noreferrer" data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${styles} ${className}`}>
      <Star size={14} /> Leave a Google review
    </a>
  );
}

// One Google review with required author attribution.
export function ReviewCard({ r, i }) {
  const [open, setOpen] = useState(false);
  const long = r.text.length > 320;
  return (
    <article className="bg-ivory border border-espresso/10 rounded-sm p-8 flex flex-col h-full" data-testid={`google-review-${i}`}>
      <Stars value={r.rating} />
      <p className="mt-5 font-light leading-relaxed text-espressosoft/85 flex-1">
        {long && !open ? `${r.text.slice(0, 320).trimEnd()}…` : r.text}
        {long && <button onClick={() => setOpen(!open)} className="ml-2 text-xs uppercase tracking-[0.14em] text-taupe link-underline">{open ? "Less" : "More"}</button>}
      </p>
      <a href={r.author_uri || r.review_uri || "#"} target="_blank" rel="noreferrer" className="mt-6 flex items-center gap-3 group">
        {r.author_photo ? <img src={r.author_photo} alt="" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full" /> : <span className="h-9 w-9 rounded-full bg-linen" />}
        <span>
          <span className="block text-sm text-espresso group-hover:underline">{r.author}</span>
          <span className="block text-xs text-taupe">{r.time} · Google <ExternalLink size={10} className="inline" /></span>
        </span>
      </a>
    </article>
  );
}

export function RatingSummary({ data, testid = "google-rating-summary" }) {
  if (!data?.rating) return null;
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <span className="font-serif text-5xl text-espresso">{data.rating.toFixed(1)}</span>
      <span>
        <Stars value={data.rating} size={18} />
        <span className="block text-xs uppercase tracking-[0.16em] text-taupe mt-1">{data.count} Google reviews</span>
      </span>
    </div>
  );
}
